import React from 'react';
import i18n from 'util/i18n';
import { extractDate } from 'components/Section/History/dateranges';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { RelationshipsCohabitantItemModel } from 'forms/sections/relationships/cohabitants/types';
import { NameModel, DateModel } from 'forms/shared/types';
import Suggestions from 'components/Form/Suggestions';
import Field from 'components/Form/Field';
import Name from 'components/Form/Name';
import DateControl from 'components/Form/DateControl';
import Show from 'components/Form/Show';
import ForeignBornDocuments from 'components/Form/ForeignBornDocuments';
import SSN from 'components/Form/SSN';
import BranchCollection from 'components/Form/BranchCollection';
import Country from 'components/Form/Country';
import Location from 'components/Form/Location';
import Comments from 'components/Form/Comments';
import { pickDate } from '../../../../validators/helpers';
import { countryString } from '../../../../validators/location';
import OtherName from './OtherName';

interface Props extends Partial<RelationshipsCohabitantItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  applicantBirthdate?: DateModel
  requireRelationshipMaritalForeignBornDocExpiration?: boolean
  spouse?: NameModel
  formType?: string
}

export default class Cohabitant extends ValidationElement<Props> {
  private cohabNameRef: React.RefObject<Name>;

  constructor(props: Props) {
    super(props);

    this.cohabNameRef = React.createRef();
  }

  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code:
        err.code.includes('required')
          ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
          : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<RelationshipsCohabitantItemModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      Birthdate: this.props.Birthdate,
      BirthPlace: this.props.BirthPlace,
      ForeignBornDocument: this.props.ForeignBornDocument,
      SSN: this.props.SSN,
      OtherNames: this.props.OtherNames,
      Citizenship: this.props.Citizenship,
      CohabitationBegan: this.props.CohabitationBegan,
      SameSpouse: this.props.SameSpouse,
      Comments: this.props.Comments,
      ContinueComments: this.props.ContinueComments,
      ...queue,
    });
  };

  clear = () => {
    this.update({
      Name: undefined,
      Birthdate: undefined,
      BirthPlace: undefined,
      ForeignBornDocument: undefined,
      SSN: undefined,
      OtherNames: undefined,
      Citizenship: undefined,
      CohabitationBegan: undefined,
      SameSpouse: false,
      SameSpouseConfirmed: false,
      Comments: undefined,
      ContinueComments: undefined,
    });
  };

  updateName = (values: RelationshipsCohabitantItemModel['Name']) => {
    const similarSpouse = this.hasSimilarSpouse(values);

    this.update({
      Name: values,
      SameSpouse: similarSpouse,
    });
  };

  /**
   * SameSpouse is a feature that checks and ensures the user isn't inputting
   * their spouse in this subsection. If the spouse seems to be identicial, it
   * double-checks with the user and when the user confirms, it clears out the
   * data.
   */
  hasSimilarSpouse = (data: NameModel) => {
    const { spouse } = this.props;

    if (!data || !spouse) { return false; }

    const {
      first, middle, last, noFirstName, noMiddleName
    } = data;
    const doesNoFirstNameMatch = spouse.noFirstName === true && noFirstName === true;
    const doesFirstMatch = first === spouse.first && (doesNoFirstNameMatch ? true : first !== '');

    const doesNoMiddleNameMatch = spouse.noMiddleName === true && noMiddleName === true;
    const doesMiddleMatch = middle === spouse.middle && (doesNoMiddleNameMatch ? true : middle !== '');
    const doesLastMatch = last === spouse.last && last !== '';

    return doesFirstMatch && doesMiddleMatch && doesLastMatch;
  };

  updateBirthdate = (values: RelationshipsCohabitantItemModel['Birthdate']) => {
    this.update({
      Birthdate: values,
    });
  };

  updateBirthPlace = (values: RelationshipsCohabitantItemModel['BirthPlace']) => {
    const additional = values?.country?.value === 'United States' ? { ForeignBornDocument: {} } : {};
    this.update({
      BirthPlace: values,
      ...additional
    });
  };

  updateForeignBornDocument = (values: RelationshipsCohabitantItemModel['ForeignBornDocument']) => {
    this.update({
      ForeignBornDocument: values,
    });
  };

  updateSSN = (values: RelationshipsCohabitantItemModel['SSN']) => {
    this.update({
      SSN: values,
    });
  };

  updateOtherNames = (values: RelationshipsCohabitantItemModel['OtherNames']) => {
    this.update({
      OtherNames: values,
    });
  };

  updateCitizenship = (values: RelationshipsCohabitantItemModel['Citizenship']) => {
    this.update({
      Citizenship: values,
    });
  };

  updateCohabitationBegan = (values: RelationshipsCohabitantItemModel['CohabitationBegan']) => {
    this.update({
      CohabitationBegan: values,
    });
  };

  updateComments = (values: RelationshipsCohabitantItemModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  updateContinueComments = (values: RelationshipsCohabitantItemModel['ContinueComments']) => {
    this.update({
      ContinueComments: values,
    });
  };

  renderSpouseSuggestion = () => {
    const { spouse } = this.props;
    const name = spouse
      ? `${spouse.first || ''} ${spouse.middle || ''} ${spouse.last || ''}`.trim()
      : '';
    return <div>{name}</div>;
  };

  onSpouseSuggestion = () => {
    this.clear();
  };

  dismissSpouseSuggestion = () => {
    this.update({
      SameSpouseConfirmed: true,
      SameSpouse: false,
    });
    // EN-5142  advances focus to next element and
    // fixes spouse suggestion alert box loop
    // @ts-ignore - Ignoring due to target ref being in JS and uses inheritance
    this.cohabNameRef?.current?.suffixRef.current?.selectRef.current?.focus();
  };

  isForeignBornDocumentationDisplayed = () => {
    const country = countryString(this.props.BirthPlace?.country);

    switch (country) {
      case 'United States':
        return false;
      case '':
        // '' means that 'Outside the U.S.' is selected, but no country entered yet
        return true;
      case undefined:
        // undefined means the form is clean and untouched
        return false;
      default:
        // only other options should be a typed country other than 'United States'
        return true;
    }
  };

  render() {
    const { requireRelationshipMaritalForeignBornDocExpiration } = this.props;
    const cohabitationBeganMinDate = pickDate([this.props.applicantBirthdate, this.props.Birthdate]);
    const attribute = `CohabitantList.items[${this.props.$index}].Item`;

    return (
      <div className="cohabitant">
        <Suggestions
          className="spouse-suggestion"
          suggestionTitle={i18n.t('relationships.cohabitant.suggestion.title')}
          suggestionParagraph={i18n.t('relationships.cohabitant.suggestion.paragraph')}
          suggestionDismissLabel={i18n.t('relationships.cohabitant.suggestion.dismissLabel')}
          suggestionLabel={i18n.t('relationships.cohabitant.suggestion.label')}
          suggestionUseLabel={i18n.t('relationships.cohabitant.suggestion.useLabel')}
          suggestions={[this.props.spouse]}
          renderSuggestion={this.renderSpouseSuggestion}
          withSuggestions={false}
          show={this.props.SameSpouse}
          onDismiss={this.dismissSpouseSuggestion}
          onSuggestion={this.onSpouseSuggestion}
        />
        <Field
          title={i18n.t('relationships.cohabitant.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Name
            name="Name"
            ref={this.cohabNameRef}
            className="cohabitant-name"
            aria-label="Provide your cohabitant's full name"
            {...this.props.Name}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
          />
        </Field>

        <Field
          help="relationships.cohabitant.help.birthdate"
          title={i18n.t('relationships.cohabitant.heading.birthdate')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="datecontrol"
          attribute={`${attribute}.Birthdate`}
        >
          <DateControl
            name="birthdate"
            className="birthdate"
            {...this.props.Birthdate}
            relationship="Other"
            onUpdate={this.updateBirthdate}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Birthdate`}
          />
        </Field>

        <Field
          title={i18n.t('relationships.cohabitant.heading.birthplace')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.BirthPlace`}
        >
          <Location
            name="birthplace"
            {...this.props.BirthPlace}
            layout={Location.BIRTHPLACE_WITHOUT_COUNTY}
            className="birthplace"
            label={i18n.t('relationships.cohabitant.label.birthplace')}
            onUpdate={this.updateBirthPlace}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.BirthPlace`}
          />
        </Field>
        <Comments
          title={i18n.t('relationships.comments.optional')}
          name={`${attribute}Comments`}
          className="optional-comment"
          attribute={`${attribute}Comments`}
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />

        <Show when={this.isForeignBornDocumentationDisplayed()}>
          <ForeignBornDocuments
            name="foreignBornDocument"
            title={i18n.t('relationships.cohabitant.heading.foreignBornDocument')}
            {...this.props.ForeignBornDocument}
            onUpdate={this.updateForeignBornDocument}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            requireExpirationDate={requireRelationshipMaritalForeignBornDocExpiration}
            attribute={`${attribute}.ForeignBornDocument`}
            formType={this.props.formType}
          />
        </Show>

        <Field
          title={i18n.t('relationships.cohabitant.heading.ssn')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.SSN`}
        >
          <SSN
            name="ssn"
            {...this.props.SSN}
            onUpdate={this.updateSSN}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.SSN`}
          />
        </Field>

        <BranchCollection
          label={i18n.t('relationships.cohabitant.heading.othernames')}
          className="cohabitant-othernames"
          appendLabel={i18n.t('relationships.cohabitant.heading.appendOthernames')}
          {...this.props.OtherNames}
          onError={this.props.onError}
          onUpdate={this.updateOtherNames}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OtherNames`}
        >
          <OtherName
            name="Item"
            bind
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            applicantMinDate={extractDate(this.props.Birthdate)}
            forceMinDate
            prefix="cohabitant"
            attribute={`${attribute}.OtherNames`}
            nameAttribute="OtherName"
          />
        </BranchCollection>

        <Field
          title={i18n.t('relationships.cohabitant.heading.citizenship')}
          help="relationships.cohabitant.help.citizenship"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="country"
          attribute={`${attribute}.Citizenship`}
        >
          <Country
            name="Citizenship"
            {...this.props.Citizenship}
            multiple
            className="relationships-cohabitant-citizenship"
            onUpdate={this.updateCitizenship}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Citizenship`}
          />
        </Field>

        <Field
          help="relationships.cohabitant.help.cohabitationBegan"
          title={i18n.t('relationships.cohabitant.heading.cohabitationBegan')}
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="datecontrol"
          attribute={`${attribute}.CohabitationBegan`}
        >
          <DateControl
            name="cohabitationBegan"
            className="cohabitation-began"
            prefix="cohabitant"
            minDate={cohabitationBeganMinDate}
            {...this.props.CohabitationBegan}
            minDateEqualTo
            onUpdate={this.updateCohabitationBegan}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CohabitationBegan`}
          />
        </Field>
        <Comments
          title={i18n.t('relationships.comments.optional')}
          name={`${attribute}ContinueComments`}
          className="optional-comment"
          attribute={`${attribute}ContinueComments`}
          {...this.props.ContinueComments}
          onUpdate={this.updateContinueComments}
          required={false}
        />
      </div>
    );
  }
}

Cohabitant.defaultProps = {
  Name: {},
  Birthdate: {},
  BirthPlace: {},
  ForeignBornDocument: {},
  SSN: {},
  OtherNames: { items: [] },
  Citizenship: {},
  CohabitationBegan: {},
  SameSpouse: false,
  Comments: {},
  ContinueComments: {},
  onUpdate: () => { },
  onError: (value, arr: ErrorCode) => arr,
  requireRelationshipMaritalForeignBornDocExpiration: true,
};
